<template>
	<div class="container">
		<div class="page">
			<!-- Header -->
			<div class="page-header flex-center row">
				<h1 class="page-title col">{{ $t('Change Sources History') }}</h1>
				<div class="page-header-right col-sm-auto col-xs-12 d-flex justify-content-end">
					<!-- Date Filter -->
					<daterangepicker
						:presets="filterDatePresets"
						:start="since"
						:end="until"
						@selected="setFilterDate"
						:key="componentKey"
					>
					</daterangepicker>
				</div>
			</div>

			<!-- Table Menu -->
			<div class="table-list-menu">
				<div class="row">
					<div class="col-auto mb-1 d-flex align-items-center">
						<!-- Change Product Source -->
						<button
							v-if="isUserCan('admin_change_source_add')"
							class="btn btn-primary btn-sm flex-center mb-0"
							@click="toggleModalChangeSource"
						>
							<i class="bi bi-arrow-left-right"></i>
							<span class="no-wrap">{{ $t('Change Product Source') }}</span>
						</button>
					</div>
					<div class="col-auto mb-1 d-flex align-items-center">
						<!-- Clear Filter -->
						<button
							type="button"
							class="btn btn-plain btn-sm"
							:class="{ 'invisible': !isFilterOn }"
							@click="clearFilter"
						>
							{{ $t('Clear Filter') }}
						</button>
					</div>
					<div class="col mb-1">
						<!-- Search Change Source Histories -->
						<div class="input-group multiselect-group">
							<div class="input-group-prepend">
								<button
									class="btn btn-outline-secondary dropdown-toggle"
									type="button"
									data-toggle="dropdown"
									aria-haspopup="true"
									aria-expanded="false"
								>
									{{ selectedSearchType }}
								</button>
								<div class="dropdown-menu">
									<a
										class="dropdown-item"
										v-for="(value, key) in searchTypes"
										:key="key"
										@click="assignSearchType(key)"
									>
										{{ value }}
									</a>
								</div>
							</div>
							<!-- Search By Product / Supplier -->
							<multiselect
								v-model="searchItem"
								:show-labels="false"
								class="form-control"
								:class="{ 'is-loading': isSearchingData }"
								track-by="_id"
								label="label"
								:custom-label="optionLabel"
								:options="searchOptions"
								:placeholder="searchPlaceholder"
								@search-change="fetchSearchData"
							>
								<span slot="noResult">
									No options matching the query
								</span>
								<span slot="noOptions">
									No options available
								</span>
							</multiselect>
						</div>
					</div>
					<div class="col-sm-auto mb-1 d-flex justify-content-end">
						<button
							v-if="isUserCan('admin_change_source_view')"
							class="btn btn-light flex-center"
							:class="{ 'is-loading': isExporting }"
							@click="exportChangeSource"
						>
							<i class="material-icons">save_alt</i>
							<span class="mr-2">Export</span>
						</button>
					</div>
				</div>
			</div>

			<div class="products-list table-responsive">
				<table class="products-list-table table table-primary table-striped mb-0">
					<thead>
						<tr class="text-bold text-left">
							<td width="5%">{{ $t('No.') }}</td>
							<td>{{ $t('Recent Product Source') }}</td>
							<td>{{ $t('Recent Supplier') }}</td>
							<td>{{ $t('Change To') }}</td>
							<td>{{ $t('New Product Source') }}</td>
							<td>{{ $t('New Supplier') }}</td>
							<td
								class="th-sort"
								:class="sortClass('created_at')"
								@click="changeSorting('created_at')"
							>
								{{ $t('Updated At') }}
							</td>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(item, idx) in items" :key="getId(item)">
							<td>{{ idx + 1 }}</td>
							<td class="mw-200">
								<div>
									<span
										class="text-primary text-bold cursor-pointer"
										:class="{ 'is-loading': isShowLoading(idx, 'product', 'recent') }"
										@click="showProductDetail(getId(item.recent_product), idx, 'recent')"
									>
										{{ item.recent_product.name }}
									</span>
								</div>
								<div class="text-nowrap">
									<span>{{ getId(item.recent_product) }}</span>
									<span
										class="cursor-pointer text-primary ml-1"
										@click="copyId(getId(item.recent_product), 'Product')"
									>
										<i class="bi bi-copy"></i>
									</span>
								</div>
							</td>
							<td class="mw-200">
								<div>
									<span
										class="text-primary text-bold cursor-pointer"
										:class="{ 'is-loading': isShowLoading(idx, 'supplier', 'recent') }"
										@click="fetchUserDetail(getId(item.recent_supplier), idx, 'recent')"
									>
										{{ item.recent_supplier.name }}
									</span>
								</div>
								<div class="text-nowrap">
									<span>{{ getId(item.recent_supplier) }}</span>
									<span
										class="cursor-pointer text-primary ml-1"
										@click="copyId(getId(item.recent_supplier))"
									>
										<i class="bi bi-copy"></i>
									</span>
								</div>
							</td>
							<td class="mw-120 text-primary text-center align-middle">
								<i class="bi bi-arrow-left-right"></i>
							</td>
							<td class="mw-200">
								<div>
									<span
										class="text-primary text-bold cursor-pointer"
										:class="{ 'is-loading': isShowLoading(idx, 'product', 'new') }"
										@click="showProductDetail(getId(item.new_product), idx, 'new')"
									>
										{{ item.new_product.name }}
									</span>
								</div>
								<div class="text-nowrap">
									<span>{{ getId(item.new_product) }}</span>
									<span
										class="cursor-pointer text-primary ml-1"
										@click="copyId(getId(item.new_product), 'Product')"
									>
										<i class="bi bi-copy"></i>
									</span>
								</div>
							</td>
							<td class="mw-200">
								<div>
									<span
										class="text-primary text-bold cursor-pointer"
										:class="{ 'is-loading': isShowLoading(idx, 'supplier', 'new') }"
										@click="fetchUserDetail(getId(item.new_supplier), idx, 'new')"
									>
										{{ item.new_supplier.name }}
									</span>
								</div>
								<div class="text-nowrap">
									<span>{{ getId(item.new_supplier) }}</span>
									<span
										class="cursor-pointer text-primary ml-1"
										@click="copyId(getId(item.new_supplier))"
									>
										<i class="bi bi-copy"></i>
									</span>
								</div>
							</td>
							<td class="mw-200">
								{{ formatDate(item.created_at) }}
							</td>
						</tr>
					</tbody>
				</table>

				<div class="list-empty table-empty" v-if="items.length === 0 && !isFetching">
					<i class="material-icons mr-2 va-m">error_outline</i>
					<span class="va-m">{{ $t('No data was found') }}</span>
				</div>
			</div>

			<!-- Table Loader -->
			<div class="table-fetch-status" v-if="isFetching">
				<div class="is-loading large dark m-auto"></div>
			</div>

			<!-- Modals -->
			<div class="modals">

				<!-- Modal Change Source -->
				<modal class="table-filter-modal" size="medium" @close="toggleModalChangeSource(false)" v-if="isShowModalChangeSource">
					<div slot="header">
						<h3>Change Product Source</h3>
					</div>
					<div slot="body">
						<div class="row">
							<div class="col-12" v-if="errors.length > 0" id="errors">
								<div class="alert alert-danger" role="alert">
									<label class="text-bold">Please Fix These Errors :</label>
									<div v-for="(error, idx) in errors" :key="idx">
										<span>{{ `${idx + 1}. ${error}` }}</span>
									</div>
								</div>
							</div>

							<div class="col-12">
								<div class="form-group">
									<label class="text-bold">Recent Source</label>
									<div class="input-group multiselect-group mb-2">
										<div class="input-group-prepend">
											<span class="input-group-text">Supplier</span>
										</div>
										<!-- Search Recent Supplier -->
										<multiselect
											v-model="recentSupplier"
											:show-labels="false"
											class="form-control"
											:class="{ 'is-loading': isSearchingRecentSupplier }"
											track-by="_id"
											label="label"
											:custom-label="optionLabel"
											:options="recentSupplierOptions"
											placeholder="Input Supplier Name Here..."
											@search-change="fetchRecentSupplierData"
										>
											<span slot="noResult">
												No options matching the query
											</span>
											<span slot="noOptions">
												No options available
											</span>
										</multiselect>
									</div>

									<div class="input-group multiselect-group">
										<div class="input-group-prepend">
											<span class="input-group-text">Product</span>
										</div>
										<!-- Search Recent Product -->
										<multiselect
											v-model="recentProduct"
											:show-labels="false"
											class="form-control"
											:class="{ 'is-loading': isSearchingRecentProduct }"
											track-by="_id"
											label="label"
											:custom-label="optionLabel"
											:options="recentProductOptions"
											placeholder="Input Product Name Here..."
											@search-change="fetchRecentProductData"
											:disabled="!recentSupplier"
										>
											<span slot="noResult">
												No options matching the query
											</span>
											<span slot="noOptions">
												No options available
											</span>
										</multiselect>
									</div>
								</div>
							</div>

							<div class="col-12 d-flex justify-content-center align-items-center">
								<i class="bi bi-arrow-down text-primary"></i>
								<span class="mx-2">Change Source To</span>
								<i class="bi bi-arrow-down text-primary"></i>
							</div>

							<div class="col-12">
								<div class="form-group">
									<label class="text-bold">New Source</label>
									<div class="input-group multiselect-group mb-2">
										<span class="input-group-text">Supplier</span>
										<!-- Search New Supplier -->
										<multiselect
											v-model="newSupplier"
											:show-labels="false"
											class="form-control"
											:class="{ 'is-loading': isSearchingNewSupplier }"
											track-by="_id"
											label="label"
											:custom-label="optionLabel"
											:options="newSupplierOptions"
											placeholder="Input Supplier Name Here..."
											@search-change="fetchNewSupplierData"
										>
											<span slot="noResult">
												No options matching the query
											</span>
											<span slot="noOptions">
												No options available
											</span>
										</multiselect>
									</div>

									<div class="input-group multiselect-group">
										<div class="input-group-prepend">
											<span class="input-group-text">Product</span>
										</div>
										<!-- Search Recent Product -->
										<multiselect
											v-model="newProduct"
											:show-labels="false"
											class="form-control"
											:class="{ 'is-loading': isSearchingNewProduct }"
											track-by="_id"
											label="label"
											:custom-label="optionLabel"
											:options="newProductOptions"
											placeholder="Input Product Name Here..."
											@search-change="fetchNewProductData"
											:disabled="!newSupplier"
										>
											<span slot="noResult">
												No options matching the query
											</span>
											<span slot="noOptions">
												No options available
											</span>
										</multiselect>
									</div>
								</div>
							</div>

						</div>
					</div>
					<div slot="footer">
						<button type="button" class="btn btn-light" @click="toggleModalChangeSource(false)">
							{{ $t('Cancel') }}
						</button>
						<button
							type="button"
							class="btn btn-primary"
							:class="{ 'is-loading': isSubmitting }"
							:disabled="isSubmitDisabled"
							@click="createHistory"
						>
							Submit
						</button>
					</div>
				</modal>
			</div>

			<!-- Modal Supplier Profile -->
			<modal-supplier-profile
				v-if="isModalSupplierProfileVisible"
				:user="userDetail"
				:is-show-stats="false"
				:is-show-bank="false"
				:is-show-identity="false"
				:isShowLocation="false"
				:isShowPicture="false"
				@close="isModalSupplierProfileVisible = false"
			/>

			<!-- Product Detail -->
			<product-item-detail
				v-if="isModalProductDetailVisible"
				:product="productDetail"
				:is-loading-detail="isFetchingProductDetail"
				@close="isModalProductDetailVisible = false"
			/>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import adminApi, {
	getChangeSourceHistories,
	createChangeSourceHistory,
	exportChangeSourceHistories,
	getUserDetail,
} from '@/api/admin';
import productApi from '@/api/products';
import userApi from '@/api/user';
import Daterangepicker from '@/components/Daterangepicker.vue';
import Modal from '@/components/Modal.vue';
import ModalSupplierProfile from '@/components/ModalSupplierProfile.vue';
import ProductItemDetail from '@/components/homepages/sections/partials/ProductItemDetail.vue';
import {
	getDateRange,
	duplicateVar,
	getAxiosErrorMessage,
	copyToClipboard,
	downloadFile,
	scrollTo,
} from '@/lib/helper';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminChangeSource',
	components: {
		Daterangepicker,
		Modal,
		ModalSupplierProfile,
		ProductItemDetail,
	},
	data() {
		return {
			page: 1,
			limit: 25,
			sort_by: 'created_at',
			sort: 'desc',
			filterDate: defaultFilterDate,
			isAll: false,
			isFetching: false,
			isShowModalChangeSource: false,
			items: [],
			searchTypes: {
				recent_supplier_id: 'Recent Supplier',
				recent_product_id: 'Recent Product',
				new_supplier_id: 'New Supplier',
				new_product_id: 'New Product',
			},
			searchType: 'recent_supplier_id',
			searchOptions: [],
			recentSupplierOptions: [],
			recentProductOptions: [],
			newSupplierOptions: [],
			newProductOptions: [],
			isSearchingData: false,
			isSearchingRecentSupplier: false,
			isSearchingRecentProduct: false,
			isSearchingNewSupplier: false,
			isSearchingNewProduct: false,
			searchItem: null,
			recentSupplier: null,
			recentProduct: null,
			newSupplier: null,
			newProduct: null,
			isExporting: false,
			componentKey: 0, // to force reload component
			isSubmitting: false,
			selectedIndex: null,
			selectedType: null, // recent, new
			userDetail: false,
			isFetchingUserDetail: false,
			isModalSupplierProfileVisible: false,
			productDetail: null,
			isFetchingProductDetail: false,
			isModalProductDetailVisible: false,
			errors: [],
		};
	},
	methods: {
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchItems(false);
				}
			}
		},
		fetchItems(reset = true) {
			if (reset) {
				this.resetItems();
			}

			this.isFetching = true;
			const params = this.params;

			const callback = (response) => {
				const items = response.data;
				this.items = this.items.concat(items);
				this.isAll = items.length < this.limit;
				this.page++;
				this.isFetching = false;
			};

			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				console.log(msg);
				this.isFetching = false;
			};

			getChangeSourceHistories(params, callback, errorCallback);
		},
		exportChangeSource() {
			this.isExporting = true;

			const params = this.params;

			const callback = (response) => {
				const url = response.data;
				if (url && url !== '') {
					downloadFile(url);
				} else {
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Export Change Source',
						text: 'Export Failed',
					});
				}
				this.isExporting = false;
			};

			const errorCallback = () => {
				this.isExporting = false;
			};

			exportChangeSourceHistories(params, callback, errorCallback);
		},
		resetItems() {
			this.items = [];
			this.isAll = false;
			this.page = 1;
		},
		formatDate(dateString) {
			const dateNumberLong = date => new Date(parseInt(date.$date.$numberLong));
			const date = typeof dateString === 'object' ? dateNumberLong(dateString) : dateString;
			const timezone = this.user ? this.user.timezone : 'Asia/Jakarta';
			const formattedDate = this.moment
				.utc(date)
				.tz(timezone)
				.format('DD-MM-YYYY - HH:mm');
			return dateString && dateString !== '' ? formattedDate : '-';
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				self.fetchItems();
			}, 1000);
		},
		resetSearch() {
			this.searchItem = null;
			this.searchOptions = [];
		},
		clearFilter() {
			if (Object.keys(this.$route.query).length > 0) {
				this.$router.replace({ query: null });
			}
			this.filterDate = defaultFilterDate;
			this.componentKey += 1; // force reload daterangepicker component
			this.resetSearch();
			this.onKeywordChange();
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const since = this.moment(this.since);
				const until = this.moment(this.until);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const format = 'YYYY-MM-DD';
			const since = this.moment(filterDate.start).format(format);
			const until = this.moment(filterDate.end).format(format);
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchItems();
		},
		copyId(id, label = 'User') {
			copyToClipboard(id);

			this.$notify({
				group: 'app',
				title: `${label} ID Copied`,
				text: id,
				type: 'success',
			});
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchItems(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		assignSearchType(key) {
			this.searchType = key;
		},
		fetchSearchData(query) {
			if (query !== '') {
				this.ooDelay(() => {
					if (this.searchType === 'recent_supplier_id' || this.searchType === 'new_supplier_id') {
						this.fetchDataSupplier(query);
					} else {
						this.fetchDataProduct(query);
					}
				}, 1000);
			}
		},
		fetchRecentSupplierData(query) {
			if (query !== '') {
				this.ooDelay(() => {
					this.fetchDataSupplier(query, 'recent');
				}, 1000);
			}
		},
		fetchRecentProductData(query) {
			if (query !== '') {
				this.ooDelay(() => {
					this.fetchDataProduct(query, 'recent');
				}, 1000);
			}
		},
		fetchNewSupplierData(query) {
			if (query !== '') {
				this.ooDelay(() => {
					this.fetchDataSupplier(query, 'new');
				}, 1000);
			}
		},
		fetchNewProductData(query) {
			if (query !== '') {
				this.ooDelay(() => {
					this.fetchDataProduct(query, 'new');
				}, 1000);
			}
		},
		toggleLoading(state, type = '', entity = '') {
			const combinedCondition = `${type}-${entity}`;

			switch (combinedCondition) {
			case 'recent-supplier':
				this.isSearchingRecentSupplier = state;
				break;
			case 'recent-product':
				this.isSearchingRecentProduct = state;
				break;
			case 'new-supplier':
				this.isSearchingNewSupplier = state;
				break;
			case 'new-product':
				this.isSearchingNewProduct = state;
				break;
			default:
				this.isSearchingData = state;
				break;
			}
		},
		fetchDataSupplier(query, type = '') {
			this.toggleLoading(true, type, 'supplier');

			const params = {
				limit: 100,
				keyword: query,
				role: 'owner',
				owner_type: 'supplier',
			};

			const callback = (response) => {
				let suppliers = response.data;

				if (suppliers.length > 0) {
					suppliers = suppliers.map((data) => {
						const newObj = {
							_id: data?._id?.$oid ?? data?._id,
							name: data?.name ?? '',
							emailOrOwner: data?.email ?? '',
							businessName: data.store?.name ?? '',
						};
						return newObj;
					});
				}

				if (type === 'recent') {
					this.recentSupplierOptions = suppliers;
				} else if (type === 'new') {
					this.newSupplierOptions = suppliers;
				} else {
					this.searchOptions = suppliers;
				}

				this.toggleLoading(false, type, 'supplier');
			};

			const errorCallback = () => {
				this.toggleLoading(false, type, 'supplier');
			};

			userApi.getUsersMinimal(params, callback, errorCallback);
		},
		fetchDataProduct(query = '', type = '') {
			this.toggleLoading(true, type, 'product');

			const params = {
				limit: 100,
				is_deleted: false,
			};

			if (query) {
				params.keyword = query;
			}

			if (type === 'recent' && this.recentSupplier) {
				const supplierIds = [this.recentSupplier?._id];
				params.supplier_ids = JSON.stringify(supplierIds);
			} else if (type === 'new' && this.newSupplier) {
				const supplierIds = [this.newSupplier?._id];
				params.supplier_ids = JSON.stringify(supplierIds);
			}

			const callback = (response) => {
				let products = response.data;

				if (products.length > 0) {
					products = products.map((data) => {
						const newObj = {
							_id: data?._id?.$oid ?? data?._id,
							name: data?.name ?? '',
							emailOrOwner: data?.supplier_name ?? '',
							businessName: data?.business_name ?? '',
						};
						return newObj;
					});
				}

				if (type === 'recent') {
					this.recentProductOptions = products;
				} else if (type === 'new') {
					this.newProductOptions = products;
				} else {
					this.searchOptions = products;
				}

				this.toggleLoading(false, type, 'product');
			};

			const errorCallback = () => {
				this.toggleLoading(false, type, 'product');
			};

			adminApi.getProducts(params, callback, errorCallback);
		},
		createHistory() {
			this.isSubmitting = true;

			const recentSupplierId = this.recentSupplier?._id;
			const recentProductId = this.recentProduct?._id;
			const newSupplierId = this.newSupplier?._id;
			const newProductId = this.newProduct?._id;

			const params = {
				recent_supplier_id: recentSupplierId,
				recent_product_id: recentProductId,
				new_supplier_id: newSupplierId,
				new_product_id: newProductId,
			};

			const callback = (response) => {
				const responseData = response?.data;

				if (responseData?.errors) {
					this.errors = responseData.errors;
					setTimeout(() => {
						scrollTo('errors');
					}, 100);

					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Change Source',
						text: response?.message || 'Please Fix Errors',
					});
				} else {
					this.fetchItems();
					this.toggleModalChangeSource(false);

					this.$notify({
						group: 'app',
						type: 'success',
						title: 'Change Source',
						text: response?.message || 'Success',
					});
				}

				this.isSubmitting = false;
			};

			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);

				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Change Source',
					text: msg || 'Something went wrong',
				});

				this.isSubmitting = false;
			};

			createChangeSourceHistory(params, callback, errorCallback);
		},
		optionLabel({ name, emailOrOwner, businessName }) {
			const label = `${name} - ${emailOrOwner} - ${businessName}`;
			return label;
		},
		resetForm() {
			this.recentSupplier = null;
			this.recentProduct = null;
			this.newSupplier = null;
			this.newProduct = null;
			this.recentSupplierOptions = [];
			this.recentProductOptions = [];
			this.newSupplierOptions = [];
			this.newProductOptions = [];
			this.errors = [];
		},
		toggleModalChangeSource(state = true) {
			this.isShowModalChangeSource = state;
			this.resetForm();
		},
		fetchUserDetail(userId, index, type) {
			this.selectedIndex = index;
			this.selectedType = type;
			this.isFetchingUserDetail = true;

			const params = {};

			const callback = (response) => {
				this.userDetail = response.data;
				this.isModalSupplierProfileVisible = true;
				this.isFetchingUserDetail = false;
			};

			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Supplier Detail',
					text: msg || 'Something went wrong',
				});

				this.isFetchingUserDetail = false;
			};

			getUserDetail(userId, params, callback, errorCallback);
		},
		showProductDetail(productId, index, type) {
			this.selectedIndex = index;
			this.selectedType = type;
			this.isFetchingProductDetail = true;

			const params = {
				product_id: productId,
				show_variation_table: true,
			};

			const callback = (response) => {
				this.productDetail = response.data;
				this.isModalProductDetailVisible = true;
				this.isFetchingProductDetail = false;
			};

			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Product Detail',
					text: msg || 'Something went wrong',
				});

				this.isFetchingProductDetail = false;
			};

			productApi.getProductDetails(productId, callback, errorCallback, params);
		},
		isShowLoading(index, entity, type) {
			return (entity === 'supplier' ? this.isFetchingUserDetail : this.isFetchingProductDetail) && this.selectedIndex === index && this.selectedType === type;
		},
		getId(item) {
			return item?._id?.$oid || item?._id;
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			filterDatePresets: 'filterDatePresets',
			isUserCan: 'isUserCan',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		params() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: startDate,
				until: endDate,
			};

			if (this.searchItem) {
				params.search_type = this.searchType;
				params.search_id = this.searchItem?._id;
			}

			return params;
		},
		isFilterOn() {
			return this.since || this.until || this.searchItem;
		},
		selectedSearchType() {
			const label = this.searchTypes[this.searchType];

			return label;
		},
		searchPlaceholder() {
			let placeholder = '';

			if (this.searchType === 'recent_supplier_id' || this.searchType === 'new_supplier_id') {
				placeholder = 'Supplier Name';
			} else {
				placeholder = 'Product Name';
			}

			return placeholder;
		},
		isSubmitDisabled() {
			return !this.recentSupplier || !this.recentProduct || !this.newSupplier || !this.newProduct;
		},
	},
	watch: {
		searchType() {
			this.resetSearch();
		},
		searchItem() {
			this.fetchItems();
		},
		recentSupplier() {
			this.recentProduct = null;
			this.fetchDataProduct('', 'recent');
		},
		newSupplier() {
			this.newProduct = null;
			this.fetchDataProduct('', 'new');
		},
	},
	created() {
		this.initFilterDate();
		this.fetchItems();
		// On Scroll Bottom
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
.no-wrap {
	white-space: nowrap;
}

.mw-120 {
	min-width: 120px;
}

.mw-200 {
	min-width: 200px;
}

.btn-outline-secondary {
	border: 1px solid #ced4da;
}

.input-group-text {
	border-top-right-radius: 0 !important;
	border-bottom-right-radius: 0 !important;
	min-width: 90px;
	display: flex;
	justify-content: center;
}
</style>

<style>
.multiselect-group > .multiselect:last-child .multiselect__tags {
	border-top-left-radius: 0 !important;
	border-bottom-left-radius: 0 !important;
}
</style>
